import { useEffect, useState } from "react";
import InnerHTML from "dangerously-set-html-content";
import isEmpty from "lodash/isEmpty";
import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { BaseRedirectLinkContainer } from "@circle-react/components/OnboardingComponents/BaseRedirectLinkContainer";
import { ResendOtpLink } from "@circle-react/components/OnboardingComponents/ResendOtpLink";
import { WebviewAuthenticationLayout } from "@circle-react/components/OnboardingPage/WebviewAuthenticationLayout";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { OnboardingLayout } from "@circle-react/components/OnboardingPage/layouts/OnboardingLayout";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import type { SignInResponse } from "@circle-react/hooks/useVerifyOtp";
import { useOnboardingWhiteLabel } from "../hooks/useOnboardingWhiteLabel";
import { OtpSignInForm } from "./OtpSignInForm";

export interface CurrentCommunityContextType {
  currentCommunity: any;
  userEmail: string;
}

export interface OtpSignInPageProps {
  setOtpSignInResponse: (response: SignInResponse) => void;
}

export const OtpSignInPage = ({ setOtpSignInResponse }: OtpSignInPageProps) => {
  const pageTitle = t("sign_in");
  const [shouldShowForm, setShouldShowForm] = useState<boolean>(false);
  const result: CurrentCommunityContextType = useCurrentCommunityContext();
  const { currentCommunity, userEmail } = result;

  const { shouldShowPoweredByCircle } =
    useOnboardingWhiteLabel(currentCommunity);

  const currentHash = window.location.hash;
  const title = t("password_confirmations.title");
  const urlParams = useRouterQueryParams();
  const isInReactNativeAuthenticatedWebview: boolean =
    !!window.webview?.isComingFromReactNativeAuthenticatedWebview ||
    window.webview.isInsideMobileWebview;
  const isInReactNativeWebview =
    !!window.webview?.isComingFromReactNativeWebview;

  useEffect(() => {
    setShouldShowForm(currentHash === "#email");
  }, [currentHash]);

  // set a placeholder title for the page when authentication using an authenticated webview on mobile
  if (isInReactNativeAuthenticatedWebview) {
    return <WebviewAuthenticationLayout />;
  }

  const renderSubtitle = () => (
    <InnerHTML
      html={t("password_confirmations.otp_login_description", {
        email: userEmail,
      })}
    />
  );

  if (!shouldShowForm) return null;

  return (
    <OnboardingLayout
      community={currentCommunity}
      step={undefined}
      title={title}
      subtitle={renderSubtitle()}
      isNarrow
      withCircleBranding={isEmpty(currentCommunity)}
      shouldShowPoweredByCircle={shouldShowPoweredByCircle}
    >
      <PageTitle title={pageTitle} />
      <div>
        <OtpSignInForm setOtpSignInResponse={setOtpSignInResponse} />
      </div>
      {!isInReactNativeWebview && (
        <div className="mt-8 flex flex-col items-center justify-center">
          <div className="mb-6" id="back-link-container">
            <BaseRedirectLinkContainer>
              <Link
                to="#"
                onClick={() =>
                  (window.location.href = authenticationAppRoutes.signUp({
                    params: urlParams,
                  }))
                }
                id="back-link"
              >
                {t("authentication_app.shared_links.back")}
              </Link>
            </BaseRedirectLinkContainer>
          </div>
          <ResendOtpLink />
        </div>
      )}
    </OnboardingLayout>
  );
};
